import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Util } from '../utils/utils.module';

export class RenderState {
  isBusy: boolean = true;
  lastBusyTime: string | null = null;
  lastStableTime: string | null = null;
  constructor(init?: Partial<RenderState>) {
    Object.assign(this, init);
  }
}

@Injectable({ providedIn: 'root' })
export class RenderMonitorService {
  private readonly STABLE_MS = 500;
  private state: RenderState = new RenderState({ lastBusyTime: this.formatNow() });
  private stateSubject = new BehaviorSubject<RenderState>(this.state);
  state$ = this.stateSubject.asObservable();
  private observer!: MutationObserver;
  private stableTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private ngZone: NgZone) { }

  startMonitoring() {
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver((mutations) => {
        if (this.onlyIndicatorMutations(mutations)) return;

        this.markBusy();
        this.scheduleStable();
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      });
    });
  }

  stopMonitoring() {
    if (this.observer) this.observer.disconnect();
    if (this.stableTimer) {
      clearTimeout(this.stableTimer);
      this.stableTimer = null;
    }
  }

  private formatNow(): string {
    return Util.formatDate(new Date(), 'YYYY-MM-DD HH:mm:ss.mmm');
  }

  private onlyIndicatorMutations(mutations: MutationRecord[]): boolean {
    const indicator = document.querySelector('.render-indicator');
    if (!indicator) return false;

    for (const m of mutations) {
      if (!indicator.contains(m.target)) return false;
      const added = Array.from(m.addedNodes);
      const removed = Array.from(m.removedNodes);
      if (added.some(n => !indicator.contains(n))) return false;
      if (removed.some(n => !indicator.contains(n))) return false;
    }
    return true;
  }

  private markBusy() {
    if (!this.state.isBusy) {
      const lastBusyTime = this.formatNow();
      //console.log('Page BUSY   ' + lastBusyTime);
      this.state = new RenderState({
        ...this.state,
        isBusy: true,
        lastBusyTime
      });
      this.ngZone.run(() => this.stateSubject.next(this.state));
    }
    if (this.stableTimer) {
      clearTimeout(this.stableTimer);
      this.stableTimer = null;
    }
  }

  private scheduleStable() {
    this.stableTimer = setTimeout(() => {
      const lastStableTime = this.formatNow();
      //console.log('Page STABLE ' + lastStableTime);
      this.state = new RenderState({
        ...this.state,
        isBusy: false,
        lastStableTime
      });
      this.ngZone.run(() => this.stateSubject.next(this.state));
    }, this.STABLE_MS);
  }
}
